import React from 'react';
import { TextField } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { FormError } from './hook-form-error';
import useDebouncedValidation from '../../hooks/use-debounced-hookform-validation';
import { QueryKeyConfig } from '../../api/api-helper';
import { LoadingIndicator } from '../loading-indicator.component';

interface InputFieldProps {
  label?: string;
  placeHolder?: string;
  name: string;
  maxLength?: number;
  size?: number;
  multiline?: boolean;
  type?: string;
  autoFocus?: boolean;
  onChange?: (value: string) => void; // Updated type for clarity
  disabled?: boolean;
  isShrink?: boolean;
  isValidateOnChange?: boolean;
  validationQueryRoute?: (value: any) => QueryKeyConfig;
  className?: string;
  required?: boolean;
}

export const FormInputMultiline = ({
  name,
  label,
  size,
  multiline,
  type,
  onChange,
  disabled,
  isShrink,
  isValidateOnChange,
  validationQueryRoute,
  className,
  required,
}: InputFieldProps): JSX.Element => {
  const { control } = useFormContext();
  const { validate, isLoading } = useDebouncedValidation({ name, validationQueryRoute });

  const handleChange = (value: string, formOnChange: (value: string) => void) => {
    formOnChange(value); // Update react-hook-form state
    if (onChange) {
      onChange(value); // Call custom onChange if provided
    }
    if (!isValidateOnChange) {
      validate(value); // Trigger validation
    }
  };

  const handleOnBlur = (value: string) => {
    validate(value); // Trigger validation on blur
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      // Allow Enter to insert a newline, prevent form submission
      e.stopPropagation(); // Prevent event from bubbling up to form
    }
  };

  return (
    <div className="form-group">
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange: formOnChange, onBlur, value } }) => (
          <>
            <TextField
              name={name}
              label={label}
              variant="outlined"
              size="small"
              fullWidth
              multiline={multiline}
              rows={size ?? 4}
              onBlur={() => {
                onBlur(); // Notify react-hook-form of blur
                handleOnBlur(value); // Trigger validation
              }}
              onChange={(e) => {
                handleChange(e.target.value, formOnChange); // Handle change
              }}
              type={type}
              value={value ?? ''} // Use react-hook-form's value
              disabled={disabled}
              InputLabelProps={{
                shrink: isShrink,
              }}
              InputProps={{
                inputProps: {
                  step: 'any',
                  min: 0,
                },
              }}
              onKeyDown={handleKeyDown}
              className={`${className ?? ''} ${required ? 'itsarequiredField' : ''} ${value ? 'has-data' : ''}`}
              id="outlined-multiline-flexible"
            />
          </>
        )}
      />
      <FormError name={name} />
    </div>
  );
};

FormInputMultiline.defaultProps = {
  label: undefined,
  maxLength: undefined,
  placeHolder: undefined,
  onChange: undefined,
};

export default FormInputMultiline;
